import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/layout/Header";
import { Board } from "@/components/game/Board";
import { NumberRack } from "@/components/game/NumberRack";
import { GameControls } from "@/components/game/GameControls";
import { WinModal } from "@/components/game/WinModal";
import { fetchDaily, fetchEndless, postComplete } from "@/lib/api";
import { loadProgress, saveProgress, clearProgress } from "@/lib/storage";
import { playSound } from "@/lib/sound";
import {
  buildCellIndex,
  mapById,
  computeCellState,
  validTargetSlots,
  chooseSlotForCell,
  choosePlacedSlotForCell,
  isWin,
  cellKey,
} from "@/lib/game/numberPlacement";

const emptyAnim = { shake: new Set(), pulse: new Set() };

export default function GamePage() {
  const { t, deviceId, settings } = useApp();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const mode = params.get("mode") === "endless" ? "endless" : "daily";
  const difficulty = ["easy", "medium", "hard"].includes(params.get("difficulty"))
    ? params.get("difficulty")
    : "medium";

  const [nonce, setNonce] = useState(0);
  const [puzzle, setPuzzle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [placements, setPlacements] = useState({});
  const [givenSet, setGivenSet] = useState(new Set());
  const [hintSet, setHintSet] = useState(new Set());
  const [lockedSet, setLockedSet] = useState(new Set());
  const [selected, setSelected] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [won, setWon] = useState(false);
  const [anim, setAnim] = useState(emptyAnim);

  const startRef = useRef(Date.now());
  const savedWinRef = useRef(false);

  // ---- fetch puzzle ----
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    setWon(false);
    savedWinRef.current = false;

    const run = async () => {
      const p = mode === "daily" ? await fetchDaily(difficulty) : await fetchEndless(difficulty);
      if (!alive) return;
      initPuzzle(p);
    };
    run().catch(() => {
      if (alive) {
        setError(true);
        setLoading(false);
      }
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, difficulty, nonce]);

  const initPuzzle = (p) => {
    const givenPl = {};
    const gset = new Set();
    p.givens.forEach((g) => {
      givenPl[g.slotId] = g.blockId;
      gset.add(g.slotId);
    });

    let pl = { ...givenPl };
    let hints = new Set();
    let hUsed = 0;
    let el = 0;

    const saved = loadProgress(p.puzzle_id);
    if (saved) {
      pl = { ...givenPl, ...(saved.placements || {}) };
      hints = new Set(saved.hintSlots || []);
      hUsed = saved.hintsUsed || 0;
      el = saved.elapsed || 0;
    }

    setPuzzle(p);
    setPlacements(pl);
    setGivenSet(gset);
    setHintSet(hints);
    setLockedSet(new Set([...gset, ...hints]));
    setHintsUsed(hUsed);
    setElapsed(el);
    setSelected(null);
    setDragging(null);
    setAnim(emptyAnim);
    startRef.current = Date.now() - el;
    setLoading(false);
  };

  // ---- timer ----
  useEffect(() => {
    if (loading || won || !puzzle) return;
    const id = setInterval(() => setElapsed(Date.now() - startRef.current), 250);
    return () => clearInterval(id);
  }, [loading, won, puzzle]);

  // ---- derived ----
  const slots = useMemo(() => puzzle?.slots || [], [puzzle]);
  const blocksById = useMemo(() => (puzzle ? mapById(puzzle.blocks) : {}), [puzzle]);
  const cellIndex = useMemo(() => (puzzle ? buildCellIndex(slots) : {}), [puzzle, slots]);
  const placedIds = useMemo(() => new Set(Object.values(placements)), [placements]);
  const rack = useMemo(
    () => (puzzle ? puzzle.blocks.filter((b) => !placedIds.has(b.id)) : []),
    [puzzle, placedIds]
  );
  const activeBlock = selected ? blocksById[selected] : dragging ? blocksById[dragging] : null;
  const validSet = useMemo(
    () => (activeBlock ? new Set(validTargetSlots(slots, placements, activeBlock.length)) : new Set()),
    [activeBlock, slots, placements]
  );

  // ---- win detection ----
  useEffect(() => {
    if (!puzzle || loading || won) return;
    if (slots.length && isWin(slots, placements, blocksById, puzzle.solution)) {
      const finalMs = Date.now() - startRef.current;
      setElapsed(finalMs);
      setWon(true);
      setSelected(null);
      if (settings.sound) playSound("win");
      if (!savedWinRef.current) {
        savedWinRef.current = true;
        postComplete({
          device_id: deviceId,
          mode,
          difficulty,
          time_ms: finalMs,
          hints_used: hintsUsed,
          date: puzzle.date || null,
        }).catch(() => {});
        clearProgress(puzzle.puzzle_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placements]);

  // ---- persist progress ----
  useEffect(() => {
    if (!puzzle || loading || won) return;
    const nonGiven = {};
    Object.entries(placements).forEach(([sid, bid]) => {
      if (!givenSet.has(sid)) nonGiven[sid] = bid;
    });
    saveProgress(puzzle.puzzle_id, {
      placements: nonGiven,
      hintSlots: [...hintSet],
      hintsUsed,
      elapsed: Date.now() - startRef.current,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placements, hintsUsed]);

  // ---- actions ----
  const sfx = (name) => {
    if (settings.sound) playSound(name);
  };

  const flash = (type, cells) => {
    if (!settings.animations) return;
    setAnim((a) => ({ ...a, [type]: new Set(cells) }));
    setTimeout(() => setAnim((a) => ({ ...a, [type]: new Set() })), 450);
  };

  const placeBlock = (slotId, blockId, fromHint = false) => {
    setSelected(null);
    setDragging(null);
    setPlacements((prev) => ({ ...prev, [slotId]: blockId }));
    const slot = slots.find((s) => s.id === slotId);
    const cells = slot.cells.map((c) => cellKey(c[0], c[1]));
    const correct = blocksById[blockId].value === puzzle.solution[slotId];
    flash(correct || fromHint ? "pulse" : "shake", cells);
    sfx(fromHint ? "hint" : correct ? "correct" : "wrong");
    if (!correct && !fromHint) {
      toast(t("wrong_placement"));
    }
  };

  const removeBlock = (slotId) => {
    setPlacements((prev) => {
      const n = { ...prev };
      delete n[slotId];
      return n;
    });
  };

  const onCellClick = (r, c) => {
    if (won) return;
    if (selected) {
      const sid = chooseSlotForCell(r, c, cellIndex, validSet);
      if (sid) placeBlock(sid, selected);
      else flash("shake", [cellKey(r, c)]);
    } else {
      const sid = choosePlacedSlotForCell(r, c, cellIndex, placements, lockedSet);
      if (sid) removeBlock(sid);
    }
  };

  const onDropCell = (r, c) => {
    if (!dragging || won) return;
    const b = blocksById[dragging];
    const vs = new Set(validTargetSlots(slots, placements, b.length));
    const sid = chooseSlotForCell(r, c, cellIndex, vs);
    if (sid) placeBlock(sid, dragging);
    setDragging(null);
  };

  const onSelect = (id) =>
    setSelected((s) => {
      const next = s === id ? null : id;
      if (next) sfx("select");
      return next;
    });

  const useHint = () => {
    if (won) return;
    const empty = slots.find((s) => !placements[s.id]);
    if (!empty) {
      toast(t("no_empty_slots"));
      return;
    }
    const val = puzzle.solution[empty.id];
    const block = rack.find((b) => b.value === val && b.length === empty.length);
    if (!block) return;
    setHintSet((h) => new Set([...h, empty.id]));
    setLockedSet((l) => new Set([...l, empty.id]));
    setHintsUsed((x) => x + 1);
    placeBlock(empty.id, block.id, true);
    toast.success(t("hint_used_toast"));
  };

  const restart = () => {
    if (!puzzle) return;
    clearProgress(puzzle.puzzle_id);
    const gp = {};
    puzzle.givens.forEach((g) => {
      gp[g.slotId] = g.blockId;
    });
    setPlacements(gp);
    setHintSet(new Set());
    setLockedSet(new Set(givenSet));
    setHintsUsed(0);
    setSelected(null);
    setElapsed(0);
    setWon(false);
    savedWinRef.current = false;
    startRef.current = Date.now();
  };

  const newEndless = () => {
    setWon(false);
    if (mode !== "endless") {
      setParams({ mode: "endless", difficulty });
    }
    setNonce((n) => n + 1);
  };

  // ---- cell renderer ----
  const cellData = (r, c) => {
    if (!puzzle || puzzle.grid[r][c] === 0) return { blocked: true };
    const st = computeCellState({
      r,
      c,
      cellIndex,
      placements,
      blocksById,
      solution: puzzle.solution,
      givenSlotIds: givenSet,
      hintSlotIds: hintSet,
    });
    const highlight =
      st.state === "empty" &&
      activeBlock &&
      (cellIndex[cellKey(r, c)] || []).some((cv) => validSet.has(cv.slotId));
    return { blocked: false, ...st, highlight };
  };

  const placedCount = Object.keys(placements).length;

  // ---- render ----
  if (loading) {
    return (
      <div className="min-h-screen bg-world">
        <Header />
        <div className="grid place-items-center py-32">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1.1, ease: "linear" }}
            className="w-10 h-10 rounded-lg border-4 border-ink border-t-coral"
          />
          <p className="mt-4 font-display font-bold text-ink/60">{t("loading")}</p>
        </div>
      </div>
    );
  }

  if (error || !puzzle) {
    return (
      <div className="min-h-screen bg-world">
        <Header />
        <div className="grid place-items-center py-32 gap-4">
          <p className="font-display font-bold text-ink/70">⚠️ {t("loading")}</p>
          <button
            data-testid="retry-btn"
            onClick={() => setNonce((n) => n + 1)}
            className="px-5 py-2.5 rounded-lg bg-coral text-white border-2 border-ink font-display font-bold shadow-brutal-sm"
          >
            {t("new_puzzle")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-world pb-10">
      <Header />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-5 space-y-5">
        <GameControls
          mode={mode}
          difficulty={difficulty}
          elapsed={elapsed}
          placed={placedCount}
          total={slots.length}
          hintsUsed={hintsUsed}
          canHint={!won && rack.length > 0}
          onHint={useHint}
          onRestart={restart}
          onNew={mode === "endless" ? newEndless : undefined}
        />

        {selected && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-sm font-bold font-display text-ink/60"
          >
            {t("select_block_hint")}
          </motion.div>
        )}

        <Board
          puzzle={puzzle}
          cellData={cellData}
          onCellClick={onCellClick}
          onDropCell={onDropCell}
          dragging={dragging}
          anim={anim}
        />

        <NumberRack
          blocks={rack}
          selected={selected}
          onSelect={onSelect}
          onDragStart={(id) => setDragging(id)}
          onDragEnd={() => setDragging(null)}
        />
      </div>

      <WinModal
        open={won}
        mode={mode}
        difficulty={difficulty}
        timeMs={elapsed}
        hintsUsed={hintsUsed}
        onPlayAgain={newEndless}
        onHome={() => navigate("/")}
        onStats={() => navigate("/stats")}
      />
    </div>
  );
}
